import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { StripeProvider, CardForm } from '@mixgoat/billing-ui';
import { refreshAccessToken } from '../../redux/thunk/authThunk';
import { refreshTier } from '../../redux/thunk/tierThunk';
import { showUpgradeModal } from '../../redux/slices/tierSlice';
import { isNative, isIOS } from '../../utils/webOnly';
import { presentError } from '../../utils/errorCodes';
import ErrorBanner from '../ErrorBanner';
import './SubscriptionTab.scss';

// Stripe's card form is an iframe that takes literal colours, so read the
// app's theme tokens off <html> when the form opens: the card form follows
// the theme (classic or v2, dark or light) instead of a fixed palette.
const stripeAppearance = () => {
    const root = document.documentElement;
    const css = getComputedStyle(root);
    const token = (name, fallback) => css.getPropertyValue(name).trim() || fallback;
    return {
        theme: root.getAttribute('data-theme') === 'light' ? 'stripe' : 'night',
        variables: {
            colorPrimary: token('--color-accent', '#ffb347'),
            colorBackground: token('--v2-surface-2', token('--color-bg-secondary', '#1b1917')),
            colorText: token('--color-text-primary', '#ece6dc'),
            colorTextSecondary: token('--color-text-secondary', '#a69e92'),
            colorDanger: token('--color-danger', '#ff6b6b'),
            fontFamily: token('--v2-font', "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"),
            borderRadius: '10px',
            spacingGridRow: '28px',
            spacingGridColumn: '20px',
        },
        rules: {
            '.Label': { marginBottom: '14px', fontSize: '13px', fontWeight: '500' },
            '.Input': { padding: '14px' },
            '.Tab': { padding: '14px' },
        },
    };
};

// Detected the same way the auth thunks do: web shim has no `db`.
const IS_WEB = typeof window !== 'undefined' && window.electron && !window.electron.db;

// Heuristic for "your access token is expired/invalid, refresh and retry."
// The auth/subscription services return varying shapes — match on either an
// HTTP-like 401 status or any message that mentions expired/invalid token.
const isAuthError = (result) => {
    if (!result || !result.error) return false;
    if (result.status === 401 || result.status === 403) return true;
    const msg = String(result.error).toLowerCase();
    return msg.includes('expired') || msg.includes('invalid token') || msg.includes('unauthorized');
};

const SubscriptionTab = () => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.auth.user);
    const accessToken = useSelector((state) => state.auth.accessToken);
    // Reconciled tier (ORs Stripe + Apple/Google IAP + comp grant + trial).
    // The subscription endpoint below is Stripe-only, so without this an
    // IAP/comp GOAT user would render as "No active subscription".
    const effectiveTier = useSelector((state) => state.tier?.effectiveTier || 'free');
    const tierReason = useSelector((state) => state.tier?.reason || 'free');
    const [loading, setLoading] = useState(true);
    const [subData, setSubData] = useState(null);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(null);
    const [view, setView] = useState('main'); // 'main' | 'cancel-confirm' | 'cancel-done' | 'update-card'

    // Update-card state — populated when user enters that flow.
    const [updateCardSecret, setUpdateCardSecret] = useState(null);
    const [updateCardKey, setUpdateCardKey] = useState(null);

    // Run an auth'd subscription call; on access-token-expired, refresh once
    // and retry. On web, the access token lives in an HttpOnly cookie that
    // the shim forwards via `credentials: include`, so passing `undefined`
    // is correct — never localStorage. On Electron the refreshed token is
    // written to localStorage by `refreshAccessToken`, so we re-read it
    // there for the retry (Redux state updates are async/stale in closure).
    const callAuthed = useCallback(async (fn) => {
        const tokenFor = (t) => IS_WEB ? undefined : t;
        let result = await fn(tokenFor(accessToken));
        if (isAuthError(result)) {
            await dispatch(refreshAccessToken());
            const freshToken = IS_WEB ? undefined : localStorage.getItem('auth:accessToken');
            result = await fn(tokenFor(freshToken));
        }
        return result;
    }, [accessToken, dispatch]);

    const fetchSubscription = useCallback(async () => {
        setLoading(true);
        setError(null);
        // try/finally, not a bare await: callAuthed THROWS when the IPC call
        // rejects (offline, dead auth service) rather than returning
        // {error}. Without this the tab sat on its spinner forever with no
        // message — the same "no failure exit" shape as the player loader.
        try {
            const result = await callAuthed((t) => window.electron.subscription.getCurrent(t));
            if (result?.error) {
                setError(presentError(result.error, { surface: 'subscription' }));
            } else {
                setSubData(result);
            }
        } catch (err) {
            setError(presentError(err, { surface: 'subscription', fallback: 'Couldn’t load your subscription. Check your connection and try again.' }));
        } finally {
            setLoading(false);
        }
    }, [callAuthed]);

    useEffect(() => {
        setView('main');
        fetchSubscription();
        // Refresh the reconciled tier too — the subscription endpoint is
        // Stripe-only, so IAP/comp GOAT status is driven by the reconciled tier.
        dispatch(refreshTier());
    }, [fetchSubscription, dispatch]);

    // Re-fetch when the user returns from the browser (handles website checkout flow).
    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === 'visible') {
                fetchSubscription();
                dispatch(refreshTier());
            }
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, [fetchSubscription, dispatch]);

    const sub = subData?.subscription || null;
    const currentPlan = sub?.plan || null;
    // A real, manageable Stripe subscription (change-plan / cancel / update-card
    // only apply to these). The backend already filters out placeholder
    // customer-only rows, so `sub` here is always a real subscription.
    const isActive = sub && ['active', 'trialing', 'past_due'].includes(sub.status);
    const isCanceled = sub?.cancelAtPeriodEnd && isActive;
    const canReactivate = isCanceled;

    // GOAT granted by a non-Stripe source (Apple/Google IAP, comp grant, or
    // trial). The Stripe-only subscription endpoint returns null for these, so
    // we lean on the reconciled tier to avoid showing them "No subscription".
    const isGoatNonStripe = !isActive && effectiveTier === 'goat';
    const isIapManaged = isGoatNonStripe && tierReason === 'iap';
    const isCompGranted = isGoatNonStripe && tierReason === 'comp';
    const isTrialGoat = isGoatNonStripe && tierReason === 'trial';
    // Truly free: no active Stripe sub and tier isn't GOAT from anywhere.
    const needsNewCheckout = !isActive && !isGoatNonStripe;

    const openManageSubscription = () => {
        // Route to the store that owns the IAP subscription so the user can
        // change plan / cancel there (Apple and Google both require this).
        if (isIOS) {
            // Settings ▸ Apple Account ▸ Subscriptions
            window.electron.openUrl('itms-apps://apps.apple.com/account/subscriptions');
        } else {
            // Google Play ▸ Subscriptions for this app
            window.electron.openUrl('https://play.google.com/store/account/subscriptions?package=com.mixgoat.app');
        }
    };

    // Currency comes from the backend now (resolved from the subscription's
    // stored price_id), not hardcoded. New customers default to USD; EU
    // customers who subscribed via geo-detected EUR see €.
    const currencySymbol = sub?.currency === 'eur' ? '€' : '$';
    const monthlyAmount = '9.99';
    const annualAmount = '89';

    const handleChangePlan = async (plan) => {
        setActionLoading('changePlan');
        setError(null);
        const result = await callAuthed((t) => window.electron.subscription.changePlan(t, plan));
        if (result?.error) {
            setError(presentError(result.error, { surface: 'subscription' }));
        } else {
            await fetchSubscription();
            dispatch(refreshTier());
        }
        setActionLoading(null);
    };

    const handleCancel = async () => {
        setActionLoading('cancel');
        setError(null);
        const result = await callAuthed((t) => window.electron.subscription.cancel(t));
        if (result?.error) {
            setError(presentError(result.error, { surface: 'subscription' }));
        } else {
            setView('cancel-done');
            await fetchSubscription();
            await dispatch(refreshAccessToken());
            dispatch(refreshTier());
        }
        setActionLoading(null);
    };

    const handleReactivate = async () => {
        setActionLoading('reactivate');
        setError(null);
        const result = await callAuthed((t) => window.electron.subscription.reactivate(t));
        if (result?.error) {
            setError(presentError(result.error, { surface: 'subscription' }));
        } else {
            await fetchSubscription();
            await dispatch(refreshAccessToken());
            dispatch(refreshTier());
        }
        setActionLoading(null);
    };

    const handleNewSubscription = () => {
        // On native (iOS/Android) digital subscriptions MUST go through the
        // in-app purchase paywall — deep-linking to an external web checkout is
        // an App Store 3.1.1 violation. Open the native RevenueCat paywall.
        if (isNative) {
            dispatch(showUpgradeModal());
            return;
        }
        // Web/desktop: deep-link to website checkout. Website then deep-links
        // back via mixgoat:// after success.
        const params = new URLSearchParams();
        if (user?.id) params.set('userId', user.id);
        if (user?.email) params.set('email', user.email);
        const checkoutUrl = `https://mixgoat.com/checkout${params.toString() ? '?' + params.toString() : ''}`;
        window.electron.openUrl(checkoutUrl);
    };

    const startUpdateCard = async () => {
        setActionLoading('setup-intent');
        setError(null);
        const result = await callAuthed((t) => window.electron.subscription.createSetupIntent(t));
        setActionLoading(null);
        if (result?.error) {
            setError(presentError(result.error, { surface: 'subscription' }));
            return;
        }
        setUpdateCardSecret(result.clientSecret);
        setUpdateCardKey(result.publishableKey);
        setView('update-card');
    };

    const handleNewCardSaved = async (result) => {
        if (!result.ok) {
            setError(presentError(result.error, { surface: 'subscription' }));
            return;
        }
        setActionLoading('update-card');
        const apiResult = await callAuthed((t) => window.electron.subscription.updatePaymentMethod(t, result.paymentMethodId));
        if (apiResult?.error) {
            setError(presentError(apiResult.error, { surface: 'subscription' }));
            setActionLoading(null);
            return;
        }
        setUpdateCardSecret(null);
        setUpdateCardKey(null);
        setView('main');
        await fetchSubscription();
        setActionLoading(null);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    };

    // ----- Update-card view -----
    if (view === 'update-card' && updateCardSecret && updateCardKey) {
        return (
            <div className="subscription-tab">
                <div className="sub-container">
                    <h2 className="sub-page-title">Update payment method</h2>
                    <div className="sub-plan-card sub-update-card-body">
                        <StripeProvider
                            publishableKey={updateCardKey}
                            clientSecret={updateCardSecret}
                            appearance={stripeAppearance()}
                        >
                            <CardForm
                                onPaymentMethod={handleNewCardSaved}
                                submitLabel="Save new card"
                                submitClassName="sub-save-card-btn"
                                errorClassName="sub-inline-error"
                                disabled={actionLoading === 'update-card'}
                            />
                        </StripeProvider>
                    </div>
                    {error && <ErrorBanner variant="inline" error={error} surface="subscription" />}
                    <button
                        className="sub-text-btn"
                        onClick={() => { setView('main'); setUpdateCardSecret(null); setUpdateCardKey(null); setError(null); }}
                    >
                        Back
                    </button>
                </div>
            </div>
        );
    }

    // ----- Cancel-confirm view -----
    if (view === 'cancel-confirm') {
        return (
            <div className="subscription-tab">
                <div className="sub-container">
                    <div className="cancel-confirm-view">
                        <div className="cancel-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                                <line x1="12" y1="9" x2="12" y2="13"/>
                                <line x1="12" y1="17" x2="12.01" y2="17"/>
                            </svg>
                        </div>
                        <h2 className="cancel-title">You'll lose access to:</h2>
                        <ul className="cancel-features-list">
                            <li>Full Mixpedia — all DJs, dates, co-play data</li>
                            <li>Smart Recommendations & For You feed</li>
                            <li>Loudness Matching (LUFS targeting)</li>
                            <li>Metadata Repair from pro databases</li>
                            <li>Smart Organize & Export Reports</li>
                            <li>Track & Artist Notifications</li>
                            <li>Priority Support</li>
                        </ul>
                        {sub?.currentPeriodEnd && (
                            <p className="cancel-access-note">
                                You'll keep GOAT access until <strong>{formatDate(sub.currentPeriodEnd)}</strong>
                            </p>
                        )}
                        {error && <ErrorBanner variant="inline" error={error} surface="subscription" />}
                        <div className="cancel-actions">
                            <button className="cancel-keep-btn" onClick={() => setView('main')}>
                                Keep GOAT
                            </button>
                            <button
                                className="cancel-confirm-btn"
                                onClick={handleCancel}
                                disabled={!!actionLoading}
                            >
                                {actionLoading === 'cancel' ? 'Canceling...' : 'Cancel subscription'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ----- Cancel-done view -----
    if (view === 'cancel-done') {
        return (
            <div className="subscription-tab">
                <div className="sub-container">
                    <div className="cancel-done-view">
                        <div className="cancel-done-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-tertiary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12"/>
                            </svg>
                        </div>
                        <h2 className="cancel-done-title">Subscription canceled</h2>
                        <p className="cancel-done-text">
                            You'll keep GOAT access until the end of your billing period.
                            Changed your mind? You can resubscribe anytime.
                        </p>
                        <button
                            className="sub-resubscribe-btn"
                            onClick={handleReactivate}
                            disabled={!!actionLoading}
                        >
                            {actionLoading === 'reactivate' ? 'Reactivating...' : 'Resubscribe to GOAT'}
                        </button>
                        <button className="cancel-done-btn" onClick={() => setView('main')}>Back to subscription</button>
                    </div>
                </div>
            </div>
        );
    }

    // ----- Main view -----
    return (
        <div className="subscription-tab">
            <div className="sub-container">
                <h2 className="sub-page-title">Subscription</h2>

                {loading && (
                    <div className="sub-loading">
                        <div className="sub-spinner" />
                    </div>
                )}

                {!loading && error && !subData && (
                    <div className="sub-error-full">
                        <p>{error}</p>
                        <button onClick={fetchSubscription}>Retry</button>
                    </div>
                )}

                {!loading && subData && (
                    <>
                        {isActive ? (
                            <div className="sub-plan-card">
                                <div className="sub-plan-card-top">
                                    <div className="sub-badge-row">
                                        <span className="sub-goat-badge">GOAT</span>
                                        {isCanceled && <span className="sub-canceling-badge">Canceling</span>}
                                        {!isCanceled && <span className="sub-active-badge">Active</span>}
                                    </div>
                                    {currentPlan && (
                                        <div className="sub-price-row">
                                            <span className="sub-price">
                                                {currencySymbol}{currentPlan === 'annual' ? annualAmount : monthlyAmount}
                                            </span>
                                            <span className="sub-period">/{currentPlan === 'annual' ? 'year' : 'month'}</span>
                                        </div>
                                    )}
                                </div>
                                {sub.currentPeriodEnd && !isCanceled && (
                                    <div className="sub-detail-row">
                                        <span className="sub-detail-label">Next billing date</span>
                                        <span className="sub-detail-value">{formatDate(sub.currentPeriodEnd)}</span>
                                    </div>
                                )}
                                {isCanceled && sub.currentPeriodEnd && (
                                    <div className="sub-detail-row">
                                        <span className="sub-detail-label">Access until</span>
                                        <span className="sub-detail-value">{formatDate(sub.currentPeriodEnd)}</span>
                                    </div>
                                )}
                            </div>
                        ) : isGoatNonStripe ? (
                            // GOAT from a non-Stripe source — App Store / Google
                            // IAP, a complimentary grant, or an active trial.
                            // There's no Stripe subscription to manage here, so
                            // we show status + where it's managed instead of the
                            // change-plan / cancel controls below.
                            <div className="sub-plan-card">
                                <div className="sub-plan-card-top">
                                    <div className="sub-badge-row">
                                        <span className="sub-goat-badge">GOAT</span>
                                        <span className="sub-active-badge">{isTrialGoat ? 'Trial' : 'Active'}</span>
                                    </div>
                                </div>
                                {isIapManaged && (
                                    <div className="sub-detail-row">
                                        <span className="sub-detail-label">Billing</span>
                                        <span className="sub-detail-value">{isIOS ? 'App Store' : 'Google Play'}</span>
                                    </div>
                                )}
                                {isCompGranted && (
                                    <div className="sub-detail-row">
                                        <span className="sub-detail-label">Plan</span>
                                        <span className="sub-detail-value">Complimentary</span>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="sub-no-subscription">
                                <p>No active subscription.</p>
                                <p className="sub-no-sub-hint">Subscribe to GOAT to unlock all features.</p>
                            </div>
                        )}

                        {/* IAP-managed: route management to the store, not Stripe */}
                        {isIapManaged && (
                            <div className="sub-section">
                                <button className="sub-text-btn" onClick={openManageSubscription}>
                                    {isIOS ? 'Manage subscription in the App Store' : 'Manage subscription in Google Play'}
                                </button>
                            </div>
                        )}

                        {/* Reactivate */}
                        {canReactivate && (
                            <div className="sub-section">
                                <button
                                    className="sub-resubscribe-btn"
                                    onClick={handleReactivate}
                                    disabled={!!actionLoading}
                                >
                                    {actionLoading === 'reactivate' ? 'Reactivating...' : 'Resubscribe to GOAT'}
                                </button>
                            </div>
                        )}

                        {/* Subscribe (new checkout / IAP paywall) — only for
                            genuinely free users, not IAP/comp/trial GOAT. */}
                        {needsNewCheckout && (
                            <div className="sub-section">
                                <button className="sub-resubscribe-btn" onClick={handleNewSubscription}>
                                    Subscribe to GOAT
                                </button>
                            </div>
                        )}

                        {/* Switch plan */}
                        {isActive && !isCanceled && (
                            <div className="sub-section">
                                <h3 className="sub-section-title">Change plan</h3>
                                <div className="sub-plan-options">
                                    <button
                                        className={`sub-plan-option ${currentPlan === 'monthly' ? 'current' : ''}`}
                                        onClick={() => handleChangePlan('monthly')}
                                        disabled={currentPlan === 'monthly' || !!actionLoading}
                                    >
                                        <div className="sub-plan-option-info">
                                            <span className="sub-plan-option-name">Monthly</span>
                                            <span className="sub-plan-option-price">{currencySymbol}{monthlyAmount}/mo</span>
                                        </div>
                                        {currentPlan === 'monthly' && <span className="sub-plan-option-current">Current</span>}
                                        {currentPlan !== 'monthly' && <span className="sub-plan-option-switch">Switch</span>}
                                    </button>
                                    <button
                                        className={`sub-plan-option ${currentPlan === 'annual' ? 'current' : ''}`}
                                        onClick={() => handleChangePlan('annual')}
                                        disabled={currentPlan === 'annual' || !!actionLoading}
                                    >
                                        <div className="sub-plan-option-info">
                                            <span className="sub-plan-option-name">Annual</span>
                                            <span className="sub-plan-option-price">{currencySymbol}{annualAmount}/yr <span className="sub-plan-save">Save 26%</span></span>
                                        </div>
                                        {currentPlan === 'annual' && <span className="sub-plan-option-current">Current</span>}
                                        {currentPlan !== 'annual' && <span className="sub-plan-option-switch">Switch</span>}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Payment & cancel */}
                        {isActive && (
                            <div className="sub-section">
                                <button className="sub-text-btn" onClick={startUpdateCard} disabled={!!actionLoading}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/>
                                    </svg>
                                    {actionLoading === 'setup-intent' ? 'Loading...' : 'Update payment method'}
                                </button>
                                {!isCanceled && (
                                    <button className="sub-text-btn danger" onClick={() => setView('cancel-confirm')}>
                                        Cancel subscription
                                    </button>
                                )}
                            </div>
                        )}

                        {error && <ErrorBanner variant="inline" error={error} surface="subscription" />}
                    </>
                )}
            </div>
        </div>
    );
};

export default SubscriptionTab;
